import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { PostMediaKind } from '../platformMeta';
import { slotLabel } from '../slotLabel';
import { SerializedSocialChannel, SocialPlatform } from '../types';
import { ComposerPreviewCard } from './ComposerPreviewCard';
import { ComposerTiming } from './useComposerDraft';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerPreviewsProps {
  platforms: SocialPlatform[];
  channels: SerializedSocialChannel[];
  captionFor: (platform: SocialPlatform) => string;
  bestSlots: Partial<Record<SocialPlatform, string>>;
  timing: ComposerTiming;
  pickedAt: string;
  mediaUrl?: string;
  mediaKind?: PostMediaKind;
}

/** The post on every channel it goes to, side by side, as the copy changes. */
const ComposerPreviewsComponent: React.FC<ComposerPreviewsProps> = ({
  platforms,
  channels,
  captionFor,
  bestSlots,
  timing,
  pickedAt,
  mediaUrl,
  mediaKind,
}) => {
  const intl = useIntl();

  const cards = useMemo(
    () =>
      platforms.map((platform) => {
        const slot = bestSlots[platform];
        let whenLabel: string;
        if (timing === 'now') {
          whenLabel = intl.formatMessage({ id: 'app.sml.promote.composer.timing.now', defaultMessage: 'Publish now' });
        } else if (timing === 'pick') {
          whenLabel =
            pickedAt === ''
              ? intl.formatMessage({ id: 'app.sml.promote.composer.timing.pick', defaultMessage: 'Pick a time' })
              : slotLabel(intl, new Date(pickedAt).toISOString());
        } else {
          whenLabel = slot === undefined ? '' : slotLabel(intl, slot);
        }
        return (
          <ComposerPreviewCard
            key={platform}
            platform={platform}
            handle={channels.find((channel) => channel.platform === platform)?.handle ?? null}
            caption={captionFor(platform)}
            mediaUrl={mediaUrl}
            mediaKind={mediaKind}
            whenLabel={whenLabel}
            slot={slot}
          />
        );
      }),
    [bestSlots, captionFor, channels, intl, mediaKind, mediaUrl, pickedAt, platforms, timing]
  );

  return (
    <div className={styles.previewRail}>
      <div className={styles.railLabel}>
        {intl.formatMessage(
          {
            id: 'app.sml.promote.composer.livePreview',
            defaultMessage: 'Live preview · {count, plural, one {# channel} other {# channels}}',
          },
          { count: platforms.length }
        )}
      </div>
      {cards.length === 0 ? (
        <p className={styles.railEmpty}>
          {intl.formatMessage({
            id: 'app.sml.promote.composer.livePreview.empty',
            defaultMessage: 'Pick a channel to see the preview.',
          })}
        </p>
      ) : (
        <div className={styles.stack}>{cards}</div>
      )}
    </div>
  );
};

export const ComposerPreviews = TypedMemo(ComposerPreviewsComponent);
